// Account → Donations data fetcher.
//
// fetchDonations() pulls the donation history + active subscription
// summary + a fresh Stripe billing-portal session URL. Called once on
// tab mount; the SPA doesn't re-fetch unless the user navigates away
// and back (donations only change via webhook, not on this page).

import { api } from './api';

export type DonationType = 'one_time' | 'monthly' | 'lifetime';
export type DonationStatus = 'succeeded' | 'failed' | 'refunded';

export interface DonationRow {
  id: number;
  type: DonationType;
  amount_cents: number;
  currency: string;
  status: DonationStatus;
  paid_at: string | null;
}

export interface ActiveSubscription {
  stripe_id: string;
  status: 'active' | 'trialing';
}

export interface DonationsPayload {
  donations: DonationRow[];
  active_subscription: ActiveSubscription | null;
  portal_url: string | null;
}

export async function fetchDonations(): Promise<DonationsPayload> {
  return api<DonationsPayload>('/api/account/donations');
}

/** "Monthly" | "One-time" | "Lifetime" — for table rendering. */
export function donationTypeLabel(type: DonationType): string {
  switch (type) {
    case 'one_time':
      return 'One-time';
    case 'monthly':
      return 'Monthly';
    case 'lifetime':
      return 'Lifetime';
  }
}

/** Format cents as a localized currency string ($5.00 / $250.00). */
export function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}
